import { useMemo, useState } from 'react';
import { deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ChevronDown, Phone, Save, Trash2 } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAdminCollection } from './hooks';
import { EmptyState, QuoteStatusBadge } from './adminUi';
import type { QuoteRequest, QuoteStatus } from '../../lib/types';
import { formatDateTimeMs } from '../../lib/format';

const STATUSES: QuoteStatus[] = ['new', 'contacted', 'quoted', 'won', 'lost'];

export default function QuotesAdmin() {
  const { items, loading } = useAdminCollection<QuoteRequest>('quotes');
  const [filter, setFilter] = useState<QuoteStatus | 'all'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  const filtered = useMemo(() => {
    const list = filter === 'all' ? items : items.filter((q) => q.status === filter);
    return [...list].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  }, [items, filter]);

  const remove = async (q: QuoteRequest) => {
    if (confirm(`Delete the request from ${q.customer.name}?`)) await deleteDoc(doc(db(), 'quotes', q.id));
  };

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="font-display text-2xl font-bold text-white">Quotes & Messages</h1>
      <p className="mt-1 text-sm text-zinc-500">
        Custom work quote requests and contact-form messages land here.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {(['all', ...STATUSES] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s as QuoteStatus | 'all')}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-all ${
              filter === s ? 'bg-gradient-to-r from-accent-600 to-sky-500 text-white' : 'glass text-zinc-400 hover:text-white'
            }`}
          >
            {s === 'all' ? `All (${items.length})` : `${s} (${items.filter((q) => q.status === s).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <EmptyState text="Loading…" />
      ) : filtered.length === 0 ? (
        <EmptyState text="Nothing here yet." />
      ) : (
        <div className="mt-6 space-y-3">
          {filtered.map((q) => {
            const open = openId === q.id;
            return (
              <div key={q.id} className={`card overflow-hidden ${open ? 'border-accent-500/40' : ''}`}>
                <button className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 p-4 text-left sm:px-5" onClick={() => setOpenId(open ? null : q.id)}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-semibold text-white">
                      {q.customer.name}
                      <span className="ml-2 font-body text-sm font-normal text-zinc-400">{q.serviceName}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-zinc-500">
                      {q.createdAt ? formatDateTimeMs(q.createdAt) : ''}{q.vehicle ? ` · ${q.vehicle}` : ''}{q.budget ? ` · budget: ${q.budget}` : ''}
                    </p>
                  </div>
                  <QuoteStatusBadge status={q.status} />
                  <ChevronDown size={17} className={`text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>

                {open && (
                  <div className="grid gap-5 border-t border-white/[0.06] p-5 sm:grid-cols-2">
                    <div className="space-y-2 text-sm">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">Contact</h3>
                      {q.customer.phone && (
                        <p className="flex items-center gap-2 text-zinc-300">
                          <Phone size={13} className="text-accent-400" />
                          <a href={`tel:${q.customer.phone}`} className="hover:text-accent-300">{q.customer.phone}</a>
                        </p>
                      )}
                      {q.customer.email && <p className="text-zinc-300">{q.customer.email}</p>}
                      <h3 className="pt-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Details</h3>
                      <p className="whitespace-pre-wrap leading-relaxed text-zinc-300">{q.details}</p>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">Status</h3>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {STATUSES.map((s) => (
                          <button
                            key={s}
                            onClick={() => updateDoc(doc(db(), 'quotes', q.id), { status: s })}
                            className={`rounded-lg border px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-all ${
                              q.status === s
                                ? 'border-accent-400/70 bg-accent-500/15 text-white'
                                : 'border-white/10 bg-white/[0.02] text-zinc-400 hover:border-white/25 hover:text-white'
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                      <h3 className="mt-5 text-xs font-bold uppercase tracking-wider text-zinc-500">Internal Notes</h3>
                      <textarea
                        className="input mt-2 min-h-24"
                        value={notesDraft[q.id] ?? q.adminNotes ?? ''}
                        onChange={(e) => setNotesDraft({ ...notesDraft, [q.id]: e.target.value })}
                      />
                      <div className="mt-3 flex items-center justify-between">
                        <button className="btn-ghost !px-4 !py-2 text-xs" onClick={() => updateDoc(doc(db(), 'quotes', q.id), { adminNotes: notesDraft[q.id] ?? q.adminNotes ?? '' })}>
                          <Save size={13} /> Save Notes
                        </button>
                        <button className="flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:text-red-300" onClick={() => remove(q)}>
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
